import React from "react";

/** Scroll a horizontal tab bar so the active pill sits in view (centered when possible). */
function scrollTabPillIntoView(bar: HTMLElement, pill: HTMLElement) {
  const maxScroll = Math.max(0, bar.scrollWidth - bar.clientWidth);
  if (maxScroll <= 0) return;
  const target =
    pill.offsetLeft + pill.offsetWidth / 2 - bar.clientWidth / 2;
  bar.scrollTo({
    left: Math.max(0, Math.min(maxScroll, target)),
    // Instant — smooth scrolling made tab switches feel laggy.
    behavior: "auto",
  });
}

/** Keep the active carousel / platform tab pill visible in horizontal tab bars. */
export function useTemplateTabScrollIntoView(
  tabBarRef: React.RefObject<HTMLDivElement | null>,
  activeTab: number,
  tabKey: string,
): void {
  React.useLayoutEffect(() => {
    if (!tabKey || activeTab < 0) return;

    const scrollActivePill = () => {
      const bar = tabBarRef.current;
      if (!bar || bar.clientWidth <= 0) return;
      const pill = bar.children.item(activeTab) as HTMLElement | null;
      if (!pill) return;
      scrollTabPillIntoView(bar, pill);
    };

    scrollActivePill();
    const raf = requestAnimationFrame(scrollActivePill);
    const t = window.setTimeout(scrollActivePill, 80);

    const bar = tabBarRef.current;
    const ro =
      bar && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scrollActivePill())
        : null;
    if (bar && ro) ro.observe(bar);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      ro?.disconnect();
    };
  }, [activeTab, tabKey, tabBarRef]);
}
